import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@/components/atoms/Typography';
import { TbCheck, TbMinus } from 'react-icons/tb';
import classNames from 'classnames';

export type Tier = {
  name: string;
  id: string;
  priceMonthly: string;
  mostPopular: boolean;
};

export type Section = {
  name: string;
  features: {
    name: string;
    tiers: Record<string, boolean | string>;
  }[];
};

export interface PricingComparatifProps {
  tiers: Tier[];
  sections: Section[];
}

export const PricingComparatif: React.FC<PricingComparatifProps> = ({ tiers, sections }: PricingComparatifProps) => {
  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-14 pt-6">
      {/* xs to lg */}
      <div className="mx-auto  max-w-md space-y-8 lg:hidden">
        {tiers.map((tier) => (
          <section key={tier.id} className={classNames(tier.mostPopular ? 'rounded-xl bg-gray-400/5 ring-1 ring-inset ring-gray-200' : '', 'p-8')}>
            <h3 id={tier.id} className="text-sm/6 font-semibold text-gray-900">
              {tier.name}
            </h3>
            <p className="mt-2 flex items-baseline gap-x-1 text-gray-900">
              <span className="text-4xl font-semibold">{tier.priceMonthly}</span>
              <span className="text-sm font-semibold">/mois</span>
            </p>

            <ul role="list" className="mt-10 space-y-4 text-sm/6 text-gray-900">
              {sections.map((section) => (
                <li key={section.name}>
                  <ul role="list" className="space-y-4">
                    {section.features.map((feature) =>
                      feature.tiers[tier.name] ? (
                        <li key={feature.name} className="flex gap-x-3">
                          <TbCheck aria-hidden="true" className="h-6 w-5 flex-none text-emerald-700" />
                          <span>
                            {feature.name}{' '}
                            {typeof feature.tiers[tier.name] === 'string' ? (
                              <span className="text-sm/6 text-gray-500">({feature.tiers[tier.name]})</span>
                            ) : null}
                          </span>
                        </li>
                      ) : null
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {/* lg+ */}
      <div className="isolate hidden lg:block">
        <div className="relative -mx-8">
          {tiers.some((tier) => tier.mostPopular) ? (
            <div className="absolute inset-x-4 inset-y-0 -z-10 flex">
              <div style={{ marginLeft: `${(tiers.findIndex((tier) => tier.mostPopular) + 1) * 25}%` }} aria-hidden="true" className="flex w-1/4 px-4">
                <div className="w-full rounded-t-xl border-x border-t border-gray-900/10 bg-gray-400/5" />
              </div>
            </div>
          ) : null}
          <table className="w-full table-fixed border-separate border-spacing-x-8 text-left">
            <colgroup>
              <col className="w-1/4" />
              <col className="w-1/4" />
              <col className="w-1/4" />
              <col className="w-1/4" />
            </colgroup>
            <thead>
              <tr>
                <td />
                {tiers.map((tier) => (
                  <th key={tier.id} scope="col" className="px-6 pt-6 xl:px-8 xl:pt-8">
                    <div className="text-sm/7 font-semibold text-gray-900">{tier.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row" />
                {tiers.map((tier) => (
                  <td key={tier.id} className="px-6 pt-2 xl:px-8">
                    <div className="flex items-baseline gap-x-1 text-gray-900">
                      <span className="text-4xl font-semibold">{tier.priceMonthly}</span>
                      <span className="text-sm/6 font-semibold">TTC/mois</span>
                    </div>
                  </td>
                ))}
              </tr>
              {sections.map((section, sectionIdx) => (
                <Fragment key={section.name}>
                  <tr>
                    <th scope="colgroup" colSpan={4} className={classNames(sectionIdx === 0 ? 'pt-8' : 'pt-16', 'pb-4 text-sm/6 font-semibold text-gray-900')}>
                      {section.name}
                      <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/10" />
                    </th>
                  </tr>
                  {section.features.map((feature) => (
                    <tr key={feature.name}>
                      <th scope="row" className="py-4 text-sm/6 font-normal text-gray-900">
                        {feature.name}
                        <div className="absolute inset-x-8 mt-4 h-px bg-gray-900/5" />
                      </th>
                      {tiers.map((tier) => (
                        <td key={tier.id} className="px-6 py-4 xl:px-8">
                          {typeof feature.tiers[tier.name] === 'string' ? (
                            <div className="text-center text-md text-gray-800">{feature.tiers[tier.name]}</div>
                          ) : (
                            <>
                              {feature.tiers[tier.name] === true ? (
                                <TbCheck aria-hidden="true" className="mx-auto size-5 text-emerald-700" />
                              ) : (
                                <TbMinus aria-hidden="true" className="mx-auto size-5 text-red-600" />
                              )}

                              <span className="sr-only" />
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
